import ProgressBar from "@/components/ProgressBar/ProgressBar";
import { Loader, styled } from "@monolith-forensics/monolith-ui";
import { UploadQueueItem } from "../../Hooks/useUploadQueue";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 100%;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 5px;
  .photo-name {
    font-size: 10px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
  .photo-error {
    font-size: 11px;
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

// Uploading and hashing run in parallel and the upload cannot complete until
// both finish, so overall progress is the slower of the two.
const overallPercent = (upload: UploadQueueItem) =>
  Math.min(upload.uploadPercent, upload.hashPercent);

const statusLabel = (upload?: UploadQueueItem) => {
  if (upload?.phase === "queued") return "Waiting...";
  if (upload?.phase === "finalizing") return "Finalizing...";
  // every byte is sent but the hash is still running
  if (upload && upload.uploadPercent >= 100 && upload.hashPercent < 100) {
    return "Hashing...";
  }
  return "Uploading...";
};

export type PhotoUploadPlaceholderProps = {
  name: string;
  /** Local queue entry. Omit for a server `uploading` row with no local state (indeterminate). */
  upload?: UploadQueueItem;
  /** Shows a dismiss button on failed uploads. */
  onDismiss?: () => void;
  /** Shows a delete button on a server `uploading` row with no local entry, so an abandoned upload can be removed. */
  onDelete?: () => void;
};

export const PhotoUploadPlaceholder = ({
  name,
  upload,
  onDismiss,
  onDelete,
}: PhotoUploadPlaceholderProps) => (
  <StyledContainer className="photo-upload-placeholder">
    {upload?.phase === "error" ? (
      <div className="photo-error">
        Upload failed{upload.error?.message ? `: ${upload.error.message}` : ""}
        {onDismiss && (
          <button type="button" onClick={onDismiss}>
            Dismiss
          </button>
        )}
      </div>
    ) : (
      <>
        <Loader size={15} />
        <div>{statusLabel(upload)}</div>
        {upload && <ProgressBar percent={overallPercent(upload)} />}
        {!upload && onDelete && (
          <button type="button" onClick={onDelete}>
            Delete
          </button>
        )}
      </>
    )}
    <div className="photo-name">{name}</div>
  </StyledContainer>
);
